/**
 * This hook is used to manage application data in local storage.
 * It provides functions to update and get any value in a flat json blob.
 */

import { useCallback } from 'react';

export function useApplicationLocalStorage<T>() {
  const getApplicationData = useCallback(() => {
    if (typeof window === 'undefined') {
      return {};
    }
    const storedData = localStorage.getItem('applicationData');
    return storedData ? JSON.parse(storedData) : {};
  }, []);

  const updateApplicationData = useCallback(
    (newData: Partial<T>) => {
      const applicationData = getApplicationData();
      const updatedData = { ...applicationData, ...newData };

      //Avoid localStorage access on the server side as it is not defined
      if (typeof window !== 'undefined') {
        localStorage.setItem('applicationData', JSON.stringify(updatedData));
      }
    },
    [getApplicationData],
  );

  const getApplicationDataByKey = useCallback(
    (key: string) => {
      const applicationData = getApplicationData();
      return applicationData[key];
    },
    [getApplicationData],
  );

  const clearApplicationData = useCallback(() => {
    if (typeof window !== 'undefined') {
      localStorage.removeItem('applicationData');
    }
  }, []);

  return {
    getApplicationData,
    updateApplicationData,
    getApplicationDataByKey,
    clearApplicationData,
  };
}
